<template>
  <div class="search-input-container">
    <div class="search-input" :class="inputClasses" ref="searchInputEl">
      <transition name="ai-prefill" mode="out-in">
        <!-- Normal search input -->
        <div v-if="!aiPrefill && !prefill" class="search-input-content" key="input">
          <vue-feather :type="icon" size="18" class="search-input-icon" v-tooltip="iconTooltip || ''" />
          <input
            type="text"
            class="search-input-field"
            :placeholder="placeholder"
            :value="modelValue"
            :disabled="disabled"
            autocomplete="nope"
            data-lpignore="true"
            data-form-type="other"
            @input="handleInput"
            @keydown.enter="handleEnter"
            @focus="isFocused = true"
            @blur="isFocused = false"
            ref="input"
          >
          <button
            v-if="clearable && modelValue"
            class="search-input-clear"
            @click="handleClear"
            type="button"
          >
            <vue-feather type="x" size="14" />
          </button>
          <div v-if="hasFilterSlot" class="search-input-filter" ref="filterContainer">
            <button
              type="button"
              class="search-input-filter-btn"
              :class="{ 'search-input-filter-btn--active': hasActiveFilter }"
              @click="handleFilterClick"
            >
              <vue-feather type="settings" size="16" />
            </button>
          </div>
        </div>
        <!-- AI Prefill chip -->
        <div v-else-if="aiPrefill" class="ai-prefill-chip" key="prefill">
          <div class="ai-prefill-chip-content">
            <img src="@/assets/icons/stars.png" alt="AI" class="ai-prefill-icon" />
            <span class="ai-prefill-value">{{ aiPrefill.value }}</span>
          </div>
          <button
            class="ai-prefill-dismiss"
            @click="dismissPrefill"
            type="button"
            title="Dismiss AI suggestion"
          >
            <vue-feather type="x" size="14" />
          </button>
        </div>
        <!-- Generic Prefill chip -->
        <div v-else class="prefill-chip" key="generic-prefill">
          <div class="prefill-chip-content">
            <vue-feather type="search" size="16" class="prefill-icon" />
            <span class="prefill-value">{{ prefill.value }}</span>
          </div>
          <button
            class="prefill-dismiss"
            @click="dismissPrefill"
            type="button"
            title="Clear filter"
          >
            <vue-feather type="x" size="14" />
          </button>
        </div>
      </transition>
    </div>
    <Teleport to="body">
      <div v-if="isFilterOpen" class="search-input-filter-menu" ref="filterMenu" :style="filterMenuStyle">
        <slot name="filter"></slot>
      </div>
    </Teleport>
  </div>
</template>

<script>
import VueFeather from 'vue-feather'

export default {
  name: 'SearchInput',
  components: {
    'vue-feather': VueFeather
  },
  props: {
    modelValue: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: 'Search...'
    },
    disabled: {
      type: Boolean,
      default: false
    },
    // Debounce delay in ms (0 = no debounce)
    debounce: {
      type: Number,
      default: 500
    },
    // Minimum characters before a non-empty value fires the search event
    // automatically (clearing to empty always fires; Enter bypasses the minimum)
    minChars: {
      type: Number,
      default: 2
    },
    // Show clear button when input has value
    clearable: {
      type: Boolean,
      default: false
    },
    // Whether a filter is currently active (shows indicator on filter icon)
    hasActiveFilter: {
      type: Boolean,
      default: false
    },
    // AI prefill configuration: { value: String }
    // The icon is always the AI stars icon
    aiPrefill: {
      type: Object,
      default: null
    },
    // Generic prefill configuration: { value: String }
    // Shows a simple pill with search icon and dismiss button
    prefill: {
      type: Object,
      default: null
    },
    // Icon to show in the search input (feather icon name)
    icon: {
      type: String,
      default: 'search'
    },
    // Tooltip text for the search icon
    iconTooltip: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      debounceTimeout: null,
      isFocused: false,
      isFilterOpen: false,
      filterPosition: { top: 0, left: 0, width: 250 }
    }
  },
  computed: {
    hasFilterSlot() {
      return !!this.$slots.filter
    },
    filterMenuStyle() {
      return {
        top: this.filterPosition.top + 'px',
        left: this.filterPosition.left + 'px',
        width: this.filterPosition.width + 'px'
      }
    },
    inputClasses() {
      return {
        'search-input--focused': this.isFocused,
        'search-input--disabled': this.disabled,
        'search-input--has-filter': this.hasFilterSlot,
        'search-input--prefill': !!this.aiPrefill || !!this.prefill
      }
    }
  },
  mounted() {
    document.addEventListener('mousedown', this.handleClickOutside)
  },
  beforeUnmount() {
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout)
    }
    document.removeEventListener('mousedown', this.handleClickOutside)
  },
  methods: {
    handleInput(event) {
      const value = event.target.value

      // If there's a prefill, dismiss it when user starts typing
      if (this.aiPrefill || this.prefill) {
        this.$emit('dismiss-prefill')
        return
      }

      this.$emit('update:modelValue', value)

      // Below the minimum length nothing fires (typing 1 char would trigger a
      // full backend search); clearing to empty still fires so lists reset
      if (value.length > 0 && value.length < this.minChars) {
        if (this.debounceTimeout) {
          clearTimeout(this.debounceTimeout)
          this.debounceTimeout = null
        }
        return
      }

      // Debounced search event
      if (this.debounce > 0) {
        if (this.debounceTimeout) {
          clearTimeout(this.debounceTimeout)
          this.debounceTimeout = null
        }
        this.debounceTimeout = setTimeout(() => {
          this.$emit('search', value)
        }, this.debounce)
      } else {
        this.$emit('search', value)
      }
    },
    dismissPrefill() {
      this.$emit('dismiss-prefill')
    },
    handleEnter() {
      // Cancel pending debounce and emit immediately
      if (this.debounceTimeout) {
        clearTimeout(this.debounceTimeout)
        this.debounceTimeout = null
      }
      this.$emit('search', this.modelValue)
    },
    handleClear() {
      this.$emit('update:modelValue', '')
      this.$emit('search', '')
      this.$emit('clear')
      this.$refs.input.focus()
    },
    handleFilterClick() {
      if (!this.isFilterOpen) {
        this.updateFilterPosition()
      }
      this.isFilterOpen = !this.isFilterOpen
    },
    updateFilterPosition() {
      const searchEl = this.$refs.searchInputEl
      const filterBtn = this.$refs.filterContainer
      if (!searchEl || !filterBtn) return
      const searchRect = searchEl.getBoundingClientRect()
      const btnRect = filterBtn.getBoundingClientRect()
      this.filterPosition = {
        top: btnRect.bottom + 8,
        left: searchRect.left,
        width: searchRect.width
      }
    },
    handleClickOutside(event) {
      const filterContainer = this.$refs.filterContainer
      const filterMenu = this.$refs.filterMenu
      const insideButton = filterContainer && filterContainer.contains(event.target)
      const insideMenu = filterMenu && filterMenu.contains(event.target)
      if (!insideButton && !insideMenu) {
        this.isFilterOpen = false
      }
    },
    closeFilter() {
      this.isFilterOpen = false
    },
    focus() {
      this.$refs.input.focus()
    }
  },
  emits: ['update:modelValue', 'search', 'clear', 'dismiss-prefill']
}
</script>

<style scoped>
.search-input-container {
  flex: 1;
}

.search-input {
  position: relative;
  display: flex;
  align-items: center;
  min-height: 45px;
  background: #FFF;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  box-sizing: border-box;
  transition: all 0.15s ease;
}

.search-input-content {
  display: flex;
  align-items: center;
  width: 100%;
  height: 100%;
}

/* AI Prefill transition */
.ai-prefill-enter-active {
  animation: ai-prefill-in 0.25s ease-out;
}

.ai-prefill-leave-active {
  animation: ai-prefill-out 0.2s ease-in;
}

@keyframes ai-prefill-in {
  0% {
    opacity: 0;
    transform: translateY(-8px) scale(0.96);
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes ai-prefill-out {
  0% {
    opacity: 1;
  }
  40% {
    opacity: 1;
  }
  100% {
    opacity: 0;
  }
}

@keyframes ai-prefill-border-out {
  0% {
    opacity: 1;
  }
  100% {
    opacity: 0;
  }
}

.ai-prefill-chip.ai-prefill-leave-active::before {
  animation: ai-prefill-border-out 0.08s ease-out forwards;
}

.search-input:hover:not(.search-input--disabled) {
  border-color: #9ca3af;
}

.search-input--focused {
  border-color: var(--color-primary, #6a16f0);
  box-shadow: 0 0 0 3px rgba(106, 22, 240, 0.1);
}

.search-input--disabled {
  background: #f3f4f6;
  opacity: 0.6;
  cursor: not-allowed;
}

/* AI Prefill chip styling */
.search-input--prefill {
  background: transparent;
  border-color: transparent;
  padding: 0;
}

.search-input--prefill:hover {
  border-color: transparent;
}

.ai-prefill-chip {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 100%;
  min-height: 45px;
  padding: 0 6px 0 14px;
  background: linear-gradient(135deg, rgba(139, 92, 246, 0.08) 0%, rgba(99, 102, 241, 0.06) 50%, rgba(168, 85, 247, 0.08) 100%);
  border: 2px solid transparent;
  border-radius: 4px;
  background-clip: padding-box;
  position: relative;
  box-sizing: border-box;
}

.ai-prefill-chip::before {
  content: '';
  position: absolute;
  inset: -2px;
  border-radius: 6px;
  padding: 2px;
  background: linear-gradient(135deg, #8b5cf6 0%, #6366f1 50%, #a855f7 100%);
  -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
  mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
  -webkit-mask-composite: xor;
  mask-composite: exclude;
  pointer-events: none;
}

.ai-prefill-chip-content {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
}

.ai-prefill-icon {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  filter: drop-shadow(0 0 4px rgba(139, 92, 246, 0.4));
}

.ai-prefill-value {
  font-size: 0.9375rem;
  font-weight: 500;
  color: #4c1d95;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ai-prefill-dismiss {
  width: 28px;
  height: 28px;
  background: rgba(139, 92, 246, 0.1);
  border: 1px solid rgba(139, 92, 246, 0.2);
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #7c3aed;
  transition: all 0.15s ease;
  flex-shrink: 0;
  margin-left: 8px;
}

.ai-prefill-dismiss:hover {
  background: rgba(139, 92, 246, 0.2);
  border-color: rgba(139, 92, 246, 0.4);
  color: #6d28d9;
  transform: scale(1.05);
}

.ai-prefill-dismiss:active {
  transform: scale(0.95);
}

.search-input-icon {
  position: absolute;
  left: 12px;
  color: #9ca3af;
  flex-shrink: 0;
  transition: color 0.15s ease;
  cursor: default;
}

.search-input--focused .search-input-icon {
  color: var(--color-primary, #6a16f0);
}

.search-input-field {
  width: 100%;
  height: 100%;
  padding: 0 12px 0 42px;
  font-size: 1rem;
  font-weight: 400;
  color: #374151;
  background: transparent;
  border: none;
  outline: none;
  border-radius: 4px;
  box-sizing: border-box;
}

.search-input-field::placeholder {
  color: #9ca3af;
}

.search-input-field:disabled {
  cursor: not-allowed;
}

.search-input-clear {
  position: absolute;
  right: 6px;
  width: 24px;
  height: 24px;
  background: #f3f4f6;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  transition: all 0.15s ease;
}

.search-input-clear:hover {
  background: #e5e7eb;
  color: #374151;
}

/* Filter styles */
.search-input--has-filter .search-input-field {
  padding-right: 50px;
}

.search-input--has-filter.search-input .search-input-clear {
  right: 46px;
}

.search-input-filter {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 6px;
}

.search-input-filter-btn {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  color: #9ca3af;
  transition: all 0.15s ease;
}

.search-input-filter-btn:hover {
  background: #f3f4f6;
  color: #6b7280;
}

.search-input-filter-btn--active {
  background: rgba(106, 22, 240, 0.1);
  color: var(--color-primary, #6a16f0);
}

.search-input-filter-btn--active:hover {
  background: rgba(106, 22, 240, 0.15);
  color: var(--color-primary, #6a16f0);
}

.search-input-filter-menu {
  position: fixed;
  z-index: 9999;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  animation: filter-menu-appear 0.15s ease-out;
}

@keyframes filter-menu-appear {
  from {
    opacity: 0;
    transform: translateY(-4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Generic Prefill chip styling — purple to match AI theme */
.prefill-chip {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 100%;
  min-height: 45px;
  padding: 0 6px 0 14px;
  background: rgba(139, 92, 246, 0.08);
  border: 1px solid rgba(139, 92, 246, 0.25);
  border-radius: 4px;
  box-sizing: border-box;
}

.prefill-chip-content {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
}

.prefill-icon {
  color: #7c3aed;
  flex-shrink: 0;
}

.prefill-value {
  font-size: 0.9375rem;
  font-weight: 500;
  color: #4c1d95;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.prefill-dismiss {
  width: 28px;
  height: 28px;
  background: rgba(139, 92, 246, 0.1);
  border: 1px solid rgba(139, 92, 246, 0.2);
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #7c3aed;
  transition: all 0.15s ease;
  flex-shrink: 0;
  margin-left: 8px;
}

.prefill-dismiss:hover {
  background: rgba(139, 92, 246, 0.2);
  border-color: rgba(139, 92, 246, 0.4);
  color: #6d28d9;
  transform: scale(1.05);
}

.prefill-dismiss:active {
  transform: scale(0.95);
}
</style>
